import * as React from 'react';
import withStyles from 'react-jss';
import { Button } from 'src/design-system/components/atoms';
import { StylesProps } from 'src/types/common.types';
import styles from './CliConsent.styles';
import {
  buildCliRedirectUrl,
  clearCliLoginContext,
  CliLoginContext,
  EXPECTING_CLI_AUTH_REDIRECT_KEY,
  getCliLoginContext,
} from 'src/utils/cli-login';
import { requestCliCode } from '@api/cli-token';
import { LocalStorageKeys, LocalStorageService } from 'src/services/localStorage';

const CLI_NAME = 'shipsy-cli';

type ViewState =
  | { kind: 'valid'; ctx: CliLoginContext; fullName: string; organisationId: string }
  | { kind: 'expired' }
  | { kind: 'empty' };

interface IProps extends StylesProps<ReturnType<typeof styles>> {}

const CliConsent = (props: IProps) => {
  const { classes } = props;
  const [view, setView] = React.useState<ViewState | null>(null);
  const [isApproving, setIsApproving] = React.useState(false);
  const [approveError, setApproveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const init = async () => {
      const ctxResult = getCliLoginContext();
      if (!ctxResult) {
        clearCliLoginContext();
        setView({ kind: 'empty' });
        return;
      }
      if (ctxResult.status === 'expired') {
        clearCliLoginContext();
        setView({ kind: 'expired' });
        return;
      }
      const fullName = (await LocalStorageService.get(LocalStorageKeys.__NAME__)) || '';
      const organisationId = (await LocalStorageService.get(LocalStorageKeys.__ORGANISATION_ID__)) || '';
      setView({ kind: 'valid', ctx: ctxResult.ctx, fullName, organisationId });
    };
    init();
  }, []);

  const onApprove = async () => {
    if (!view || view.kind !== 'valid') return;
    const { ctx } = view;
    setIsApproving(true);
    setApproveError(null);

    window.sessionStorage.setItem(EXPECTING_CLI_AUTH_REDIRECT_KEY, '1');

    const response = await requestCliCode({
      cli_name: CLI_NAME,
      device_hint: window.navigator.platform || '',
      code_challenge: ctx.code_challenge,
    });

    if (response.isSuccess) {
      window.sessionStorage.removeItem(EXPECTING_CLI_AUTH_REDIRECT_KEY);
      const code = response.data?.code;
      if (!code) {
        setApproveError('Invalid response from server. Please try again.');
        setIsApproving(false);
        return;
      }
      clearCliLoginContext();
      window.location.href = buildCliRedirectUrl(ctx, { code, state: ctx.state }, view.organisationId);
      return;
    }

    // On 401, the global interceptor has scheduled a redirect to /login and
    // cleared localStorage. The expecting_auth_redirect flag must remain set
    // so Login.tsx preserves cli_login.ctx across the redirect.
    if (response.status === 401) {
      return;
    }

    window.sessionStorage.removeItem(EXPECTING_CLI_AUTH_REDIRECT_KEY);
    setApproveError(response.errorMessage || 'Failed to authorize CLI. Please try again.');
    setIsApproving(false);
  };

  const onDeny = () => {
    if (!view || view.kind !== 'valid') {
      clearCliLoginContext();
      window.location.href = '/';
      return;
    }
    const { ctx, organisationId } = view;
    const redirectUrl = buildCliRedirectUrl(ctx, { error: 'access_denied', state: ctx.state }, organisationId);
    clearCliLoginContext();
    window.location.href = redirectUrl;
  };

  const goToDashboard = () => {
    window.location.href = '/';
  };

  if (view === null) {
    return <div className={classes.pageContainer} />;
  }

  if (view.kind === 'empty') {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.card}>
          <h2 className={classes.title}>No CLI login in progress</h2>
          <p className={classes.emptyStateMessage}>
            There is no CLI login request to authorize in this tab. To authorize a CLI, please re-run the login command from your terminal.
          </p>
          <div className={classes.actions}>
            <Button type="primary" onClick={goToDashboard}>Go to dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  if (view.kind === 'expired') {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.card}>
          <h2 className={classes.title}>CLI login session expired</h2>
          <p className={classes.emptyStateMessage}>
            Your CLI authentication request expired. You have been signed in on this browser successfully. To authorize the CLI, please re-run the login command from your terminal.
          </p>
          <div className={classes.actions}>
            <Button type="primary" onClick={goToDashboard}>Go to dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.pageContainer}>
      <div className={classes.card}>
        <h2 className={classes.title}>Authorize CLI access</h2>
        <p className={classes.subtitle}>
          A command-line tool is requesting access to your account. Approve only if you just ran a CLI login command yourself.
        </p>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Signed in as</span>
          <span className={classes.detailValue}>{view.fullName}</span>
        </div>

        {approveError ? <p className={classes.errorMessage}>{approveError}</p> : null}

        <div className={classes.actions}>
          <Button type="secondary" onClick={onDeny} disabled={isApproving}>Deny</Button>
          <Button type="primary" onClick={onApprove} loading={isApproving}>Approve</Button>
        </div>
      </div>
    </div>
  );
};

export default withStyles(styles, { injectTheme: true })(CliConsent);
